<ng-container *transloco="let t">
  @if (strategy(); as strategy) {
    <div appNavBreadcrumb class="flex items-center">
      @if (strategyGroup() && !isSingleStrategyInStrategyGroup()) {
        <a
          iButton
          [routerLink]="'/advertising/sponsored-product/strategy-group/' + strategyGroup()!.strategyGroupId"
          [iTooltip]="t('strategy-page.back_to_strategy_group')"
          variant="ghost"
          size="lg"
          queryParamsHandling="merge"
          testId="strategy-name"
          >{{ strategyGroup()!.strategyGroupName }}</a
        >
        <span class="text-gray-300">/</span>
      }
      <app-editable-text
        [label]="strategy.name"
        [tooltipText]="t('sp-strategy-group-page.change_strategy_group_name')"
        [disable]="isReadOnly()"
        (textChange)="changeName($event)"
      />
    </div>
    <div class="flex h-full flex-col">
      @if (isStrategyBrandDeleted()) {
        <div class="alert alert-warning">
          {{ t("strategy-page.brand_removed") }}
        </div>
      }
      <div class="flex flex-col justify-start gap-2 pl-5">
        <div class="flex justify-between">
          <div class="flex items-start gap-2">
            <app-state-badge
              [state]="strategy.state"
              (stateBadgeClick)="changeStatus()"
              [disabled]="isReadOnly() || statusChangeDisabled()"
              [loading]="changeStatusLoading()"
            />
            @if (infoMessageLastUpdate(); as message) {
              <app-sync-info [message]="message" />
            }
          </div>
          <div>
            @if (strategy.primeDayBoost) {
              @if (boostActivated$ | async) {
                <div
                  class="flex items-center rounded-md border border-green-300 bg-green-50 px-2 py-1 font-medium text-green-500"
                >
                  <IIcon name="boost" class="mr-2" />
                  <span class="text-sm">{{ t("strategy-page.boost_activated") }}</span>
                </div>
              } @else if (promoDaysBoostStart()) {
                <div class="flex items-center rounded-md border bg-gray-50 px-2 py-1 font-medium text-gray-500">
                  <IIcon name="boost" class="mr-2" />
                  <span class="text-sm">{{
                    t("strategy-page.boost_planned", [promoDaysBoostStart() | date: "shortDate" : undefined : locale()])
                  }}</span>
                </div>
              }
            }
          </div>
        </div>
        <div class="flex">
          <app-strategy-page-warning />
        </div>
      </div>

      <div class="mt-3 flex justify-between px-3">
        <div class="flex gap-3">
          <app-strategy-info-bloc
            (strategyInfoClick)="editStrategyNameModal(editStrategyNameRef)"
            [disabled]="isReadOnly() || strategy.defaultStrategy"
            testid="info-strategy-name"
          >
            <div title class="text-sm text-gray-500 uppercase">{{ t("sp-strategy-creation.strategy_name") }}</div>
            <div body class="font-medium">
              @if (strategy.name) {
                {{ strategy.name }}
              } @else {
                -
              }
            </div>
          </app-strategy-info-bloc>

          @if (withAlgoMode()) {
            <app-strategy-info-bloc
              (strategyInfoClick)="changeAlgoMode()"
              [disabled]="isReadOnly() || !!strategy.primeDayBoost || strategy.algoMode === AlgoModes.TACOS_TARGET"
              testid="info-algo-mode"
              [iTooltip]="strategy.primeDayBoost ? t('strategy-page.cannot_change_when_boosted') : ''"
            >
              <div title class="text-sm text-gray-500 uppercase">
                {{ t("sd-strategy-creation.algorithm") }}
              </div>
              <div body class="font-medium">
                {{ t(AlgoModeStrKey[strategy.algoMode].shortDescription) }}
              </div>
            </app-strategy-info-bloc>
          }

          <app-strategy-page-algo-target
            [strategy]="strategy"
            [organizationId]="organizationId()"
            [isReadOnly]="isReadOnly() || !!programmaticGuaranteedDeal()"
            [currencyCode]="currencyCode()"
            [tooltip]="programmaticGuaranteedDeal() ? t('dsp-supply.programmatic_guaranteed_deal_description') : ''"
          />

          @if (strategy.algoMode === AlgoModes.MONTHLY_BUDGET_TARGET) {
            <app-strategy-info-bloc [disabled]="true">
              <div title class="text-sm text-gray-500 uppercase">{{ t("strategy-page.current_spend") }}</div>
              <div body>
                <app-spend-progress-bar
                  [spend]="currentMonthSpend()!"
                  [budget]="strategy.monthlyBudget!"
                  [endDate]="endOfMonth()"
                  [currencyCode]="currencyCode()"
                  [locale]="locale()"
                />
              </div>
            </app-strategy-info-bloc>
          }

          @if (strategy.algoMode === AlgoModes.AMAZON_MANAGED_BUDGET && strategy.budgetFlight) {
            <app-strategy-info-bloc [disabled]="true">
              <div title class="text-sm text-gray-500 uppercase">{{ t("strategy-page.current_spend") }}</div>
              <div body>
                <app-spend-progress-bar
                  [spend]="budgetFlightSpend()!"
                  [budget]="strategy.budgetFlight.budget!"
                  [endDate]="budgetFlightEndDate()"
                  [currencyCode]="currencyCode()"
                  [locale]="locale()"
                />
              </div>
            </app-strategy-info-bloc>
          }

          @if (strategy.constraint && fblMode[strategy.constraint!]) {
            <app-strategy-info-bloc [disabled]="true">
              <div title class="text-sm text-gray-500 uppercase">
                {{ t("strategy-page.last_algorithm_mode") }}
              </div>
              <div body class="mt-1 inline-flex">
                <app-fbl-mode [strategy]="strategy" [size]="FblModeSize.small" />
              </div>
            </app-strategy-info-bloc>

            @if (strategyGroup()) {
              <app-strategy-info-bloc [disabled]="true">
                <div title class="text-sm text-gray-500 uppercase">
                  {{ t("strategy-page.strategy_type") }}
                </div>
                <div body>
                  <span class="font-medium">{{ StrategyTypeStr[strategy.strategyType] }}</span>
                </div>
              </app-strategy-info-bloc>
            }
          }
          <app-strategy-page-dsp-funnel [strategy]="strategy" />
          <app-strategy-page-dsp-creative-type [strategy]="strategy" />
        </div>
      </div>

      <ng-template #editStrategyNameRef>
        <IFormField class="min-w-96">
          <label iLabel>{{ t("sp-strategy-creation.strategy_name") }}</label>
          <input
            iInput
            class="w-full"
            [ngModel]="strategy.name"
            data-testid="update-strategy-name-input"
            placeholder="{{ t('sp-strategy-creation.strategy_name') }}"
            [maxLength]="80"
            (ngModelChange)="this.newStrategyName = $event"
            autoFocus
          />
        </IFormField>
        <div class="mt-4 flex justify-end gap-2">
          <button iButton (click)="strategyTitleModal?.close()" variant="secondary">{{ t("common.cancel") }}</button>
          <button iButton (click)="changeName(newStrategyName)" data- testId="btn-confirm">
            {{ t("strategy-page.update_strategy_name") }}
          </button>
        </div>
      </ng-template>
      <div class="my-6">
        <ITabs [tabs]="tabs()" />
      </div>
      <div class="flex-1 px-1">
        <router-outlet />
      </div>
    </div>
  }
</ng-container>
